var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { FORM_BUILDER_LAYOUT_ELEMENT_TYPE, GroupOneName } from "../../utils/form-builder.types";
import { FormBuilderTranslatorService } from "../../services/translator/form-builder.translator.service";
import FormBuilderFormInterfaceSubmitButton from "./interface/submit-button/form-builder.form.interface.submit-button";
import FormBuilderFormInterfaceSuccessMessage from "./interface/success-message/form-builder.form.interface.success-message";
import FormBuilderFormEmpty from "./empty/form-builder.form-empty";
import { isoLanguageList } from "../../../../services/functions/language/language";
import useFormAnalytics from "../../../../components/hooks/use-form-analytics/use-form-analytics";
import Logger from "../../../../services/logger/logger";
import { POST_SUBMIT_ACTION } from "../../../../../../../client-scripts/form-builder";
import { useRenderElement } from "../../hooks/use-render-element";
import { getCurrentFormLanguage } from "../../helpers/helpers";
const formSubmissionHeaders = {
  deviceFingerprint: "X-TINT-Device-Fingerprint",
  tintAnonymousUid: "X-TINT-Anonymous-UID",
  jwtToken: "Authorization",
  metadata: "X-TINT-Metadata"
};
export const FormBuilderForm = ({
  injectedData,
  language,
  tintAnonymousUid,
  element,
  formName = "tint-form-name",
  tintAnalyticsClient
}) => {
  var _a, _b;
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [instanceT, setInstanceT] = useState(() => () => "");
  const [pendingComponents, setPendingComponents] = useState({});
  const submitButtonRef = useRef();
  const isAnyComponentsPending = useMemo(() => {
    for (let key in pendingComponents) {
      if (pendingComponents[key] === true) {
        return true;
      }
    }
    return false;
  }, [pendingComponents]);
  const {
    uploadedImages,
    included,
    locales: translations,
    groups,
    layout,
    formValues,
    teamId,
    formId,
    apiUrl,
    noTrack,
    tracking_enabled: trackingEnabled,
    metadata,
    accessToken
  } = injectedData;
  const elements = (_a = groups == null ? void 0 : groups[GroupOneName]) == null ? void 0 : _a.elements;
  const [actionUrl, setActionUrl] = useState(
    `${apiUrl}/v2/teams/${teamId}/forms/${formId}/form_submissions?include=files&locale=${language == null ? void 0 : language.replace("_", "-")}`
  );
  const analyticForm = useFormAnalytics({
    formName,
    formId,
    teamId,
    tintAnonymousUid,
    tracker: tintAnalyticsClient,
    noTrack,
    featureFlag: trackingEnabled
  });
  useEffect(() => {
    const currentLocale = getCurrentFormLanguage(language, translations);
    const { language: locale, i18Instance } = FormBuilderTranslatorService.init(
      JSON.parse(JSON.stringify(translations)),
      currentLocale
    );
    setActionUrl(
      `${apiUrl}/v2/teams/${teamId}/forms/${formId}/form_submissions?include=files&locale=${locale == null ? void 0 : locale.replace("_", "-")}`
    );
    setInstanceT((state) => (i18Instance == null ? void 0 : i18Instance.t) || state);
  }, [language, apiUrl, teamId, formId, translations]);
  useEffect(() => {
    if (element) {
      try {
        element.dispatchEvent(new CustomEvent("afterLoad"));
        if (tintAnalyticsClient) {
          tintAnalyticsClient.recordTraits({ embed_url: window.location.href, team_id: teamId, form_id: formId });
          tintAnalyticsClient == null ? void 0 : tintAnalyticsClient.track("form-builder-loaded", { form_id: formId });
        }
      } catch (error) {
        console.error("Error in onLoad function", error);
      }
    }
    analyticForm == null ? void 0 : analyticForm.trackFormViewed();
  }, [formId, teamId, element, analyticForm, tintAnalyticsClient]);
  const formValuesObj = new URLSearchParams(formValues);
  const resetButtonValidity = () => {
    submitButtonRef.current.setCustomValidity("");
  };
  const handleSubmit = (e) => __async(null, null, function* () {
    var _a2, _b2, _c, _d, _e;
    e.preventDefault();
    if (isSubmitted) return;
    setIsSubmitted(true);
    if (!element.dispatchEvent(new CustomEvent("beforeSubmit", { cancelable: true }))) {
      setIsSubmitted(false);
      return;
    }
    const headers = {};
    const formData = new FormData(e.target);
    const filteredFormData = new FormData();
    for (const [key, value] of formData.entries()) {
      if (!(value instanceof File)) {
        filteredFormData.append(key, value);
      }
    }
    const deviceFingerprint = window.bbDeviceFingerprint;
    const zinreloToken = accessToken || (window.eudSettings ? window.accessToken : (_b2 = (_a2 = window.zrl_mi) == null ? void 0 : _a2.init_data) == null ? void 0 : _b2.jwt_token);
    if (tintAnonymousUid) headers[formSubmissionHeaders.tintAnonymousUid] = tintAnonymousUid;
    if (deviceFingerprint) headers[formSubmissionHeaders.deviceFingerprint] = deviceFingerprint;
    if (zinreloToken) headers[formSubmissionHeaders.jwtToken] = `Bearer ${zinreloToken}`;
    if (metadata) headers[formSubmissionHeaders.metadata] = metadata;
    try {
      const response = yield fetch(actionUrl, {
        method: "POST",
        body: filteredFormData,
        headers,
        referrerPolicy: "no-referrer-when-downgrade"
      });
      const responseq = yield response.json();
      if (!response.ok) {
        const error = (_c = responseq == null ? void 0 : responseq.errors) == null ? void 0 : _c[0];
        throw new Error((error == null ? void 0 : error.detail) || (error == null ? void 0 : error.title) || (error == null ? void 0 : error.status));
      }
      try {
        setShowSuccessMessage(
          ((_d = layout == null ? void 0 : layout.postSubmit) == null ? void 0 : _d.action) === POST_SUBMIT_ACTION.SHOW_SUCCESS_MESSAGE || !((_e = layout == null ? void 0 : layout.postSubmit) == null ? void 0 : _e.action)
        );
        (() => __async(null, null, function* () {
          yield analyticForm == null ? void 0 : analyticForm.trackFormSubmitted();
          element.dispatchEvent(new CustomEvent("afterSubmit", { detail: responseq }));
        }))();
      } catch (error) {
        Logger.error("Error in onLoad function", error);
      }
    } catch (error) {
      submitButtonRef.current.setCustomValidity(error.message);
      submitButtonRef.current.reportValidity();
      setIsSubmitted(false);
      Logger.error("There was an error submitting the form!", error);
    }
  });
  const direction = ((_b = isoLanguageList[language]) == null ? void 0 : _b.direction) || "ltr";
  const { renderElement } = useRenderElement({
    translateFn: instanceT,
    teamId,
    formId,
    apiUrl,
    included,
    setPendingComponents,
    language,
    uploadedImages,
    formValuesObj
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, (elements == null ? void 0 : elements.length) === 0 ? /* @__PURE__ */ React.createElement(FormBuilderFormEmpty, null) : /* @__PURE__ */ React.createElement(
    "form",
    {
      dir: direction,
      name: formName,
      className: "div div-form",
      method: "POST",
      action: actionUrl,
      encType: "multipart/form-data",
      onSubmit: handleSubmit
    },
    /* @__PURE__ */ React.createElement("div", null, showSuccessMessage ? /* @__PURE__ */ React.createElement(
      FormBuilderFormInterfaceSuccessMessage,
      {
        item: layout == null ? void 0 : layout[FORM_BUILDER_LAYOUT_ELEMENT_TYPE.SUCCESS_MESSAGE],
        t: instanceT
      }
    ) : /* @__PURE__ */ React.createElement(React.Fragment, null, elements == null ? void 0 : elements.map((item, i) => {
      return /* @__PURE__ */ React.createElement(Fragment, { key: i }, renderElement(item));
    }), /* @__PURE__ */ React.createElement(
      FormBuilderFormInterfaceSubmitButton,
      {
        disabled: isAnyComponentsPending,
        item: layout == null ? void 0 : layout[FORM_BUILDER_LAYOUT_ELEMENT_TYPE.SUBMIT_BUTTON],
        t: instanceT,
        onClick: resetButtonValidity,
        ref: submitButtonRef
      }
    )))
  ));
};
